import type { ApexOptions } from 'apexcharts';
import { Suspense, lazy, type ReactNode } from 'react';
import { LoadingData } from '../LoadingData';

/**
 * Lazily loaded so apexcharts stays out of the initial bundle.
 *
 * It is ~500 KB minified and exactly one route draws a chart, so importing it at module
 * scope would put it in front of every sign-in. `React.lazy` keeps it on the analytics
 * chunk; the `Suspense` fallback below is what shows while it arrives.
 */
const ReactApexChart = lazy(() => import('react-apexcharts'));

export type ApexChartType = 'line' | 'bar' | 'area' | 'donut' | 'radialBar';

export type ApexChartProps = {
  type: ApexChartType;
  options: ApexOptions;
  series: ApexOptions['series'];
  height?: number;
  /** Announced to screen readers, which cannot read the SVG. */
  label: string;
};

/**
 * The one place react-apexcharts is rendered.
 *
 * ### Why the series is passed twice
 *
 * apexcharts reads its series from `options.series` internally but react-apexcharts wants
 * it as a prop, and the two disagreeing is a silent blank chart. Taking one `series` here
 * and writing it to both is what keeps them in step — the legacy screen kept the series
 * inside a `ChartOptions` blob and passed `options.series` at each of six call sites.
 *
 * ### Sizing
 *
 * Width is always `100%`. The legacy read `ref.current?.clientWidth - 50` on the first
 * render and handed apexcharts a pixel number, so every chart was sized from a layout
 * measurement taken before its container had settled and never re-measured except through
 * a `useWindowDimensions` hook that recomputed the whole options object. apexcharts
 * already resizes itself to a percentage width.
 */
export function ApexChart({ type, options, series, height = 330, label }: ApexChartProps) {
  return (
    <div role="img" aria-label={label}>
      <Suspense fallback={<LoadingData loadingLabel="Loading chart..." />}>
        <ReactApexChart
          type={type}
          options={{ ...options, series }}
          series={series}
          height={height}
          width="100%"
        />
      </Suspense>
    </div>
  );
}

export type ChartCardProps = {
  title: string;
  /** Right-aligned caption — the date range the chart covers. */
  caption?: string;
  /** Rendered instead of the chart when there is nothing to draw. */
  emptyMessage?: string;
  isEmpty?: boolean;
  isLoading?: boolean;
  children: ReactNode;
};

/**
 * Card chrome shared by every chart on a dashboard: heading, date-range caption, and the
 * loading and empty states.
 *
 * The empty state renders a message rather than an empty set of axes. An apexcharts bar
 * chart with no series still draws its grid, which reads as "zero everywhere" rather than
 * "nothing was returned" — two different things when the operator is looking at revenue.
 */
export function ChartCard({
  title,
  caption,
  emptyMessage = 'No data for this selection.',
  isEmpty,
  isLoading,
  children,
}: ChartCardProps) {
  return (
    <section className="flex h-full flex-col rounded-[14px] border border-ds-border-default bg-ds-bg-surface p-4">
      <header className="mb-4 flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-bold text-ds-text-primary">{title}</h3>
        {caption && <span className="text-xs text-ds-text-secondary">{caption}</span>}
      </header>
      {isLoading ? (
        <LoadingData />
      ) : isEmpty ? (
        <p className="py-12 text-center text-sm text-ds-text-secondary">{emptyMessage}</p>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      )}
    </section>
  );
}
